'use client'

import { BrandBanner } from '@/components/brand/BrandBanners'
import { useBrandSlugPage } from '@/features/brand/hooks/useBrandSlugPage'
import { Button } from '@/shared/components/ui/Button'
import ProductCard from '@/shared/components/ui/product-card/ProductCard'
import { BRAND_BANNERS } from '@/shared/constants/brandBanners'
import { ChevronRight } from 'lucide-react'
import Link from 'next/link'

export default function BrandSlugPage({ slug }: { slug: string }) {
  const {
    brand,
    products,
    totalProducts,
    totalPages,
    loading,
    loadingMore,
    hasMore,
    setPage,
  } = useBrandSlugPage(slug)

  const bannerKey = slug.replace(/-/g, '')
  const mobileBanners =
    BRAND_BANNERS[bannerKey as keyof typeof BRAND_BANNERS]?.mobile ?? []
  const tabletBanners =
    BRAND_BANNERS[bannerKey as keyof typeof BRAND_BANNERS]?.tablet ?? []
  const desktopBanners =
    BRAND_BANNERS[bannerKey as keyof typeof BRAND_BANNERS]?.desktop ?? []

  const brandName =
    brand?.name || slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

  return (
    <div className="bg-white min-h-full">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100 py-2">
        <div className="container mx-auto px-4 sm:px-6 flex items-center gap-2 text-[11px] font-medium text-gray-500">
          <Link href="/" className="hover:text-[#001F5F]">
            Home
          </Link>
          <ChevronRight className="w-3 h-3" />
          <Link href="/brands" className="hover:text-[#001F5F]">
            Brands
          </Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#001F5F]">{brandName}</span>
        </div>
      </div>

      {/* Mobile banners 1 & 2 */}
      <div className="md:hidden w-full">
        {mobileBanners.slice(0, 2).map((banner, i) => (
          <BrandBanner key={i} banner={banner} />
        ))}
      </div>

      {/* Tablet banners 1 & 2 */}
      <div className="hidden md:block lg:hidden w-full">
        {tabletBanners.slice(0, 2).map((banner, i) => (
          <BrandBanner key={i} banner={banner} />
        ))}
      </div>

      {/* Desktop banners 1 & 2 */}
      <div className="hidden lg:block w-full">
        {desktopBanners.slice(0, 2).map((banner, i) => (
          <BrandBanner key={i} banner={banner} />
        ))}
      </div>

      {/* Products Grid */}
      <div className="pt-8 pb-0">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-12 h-12 border-4 border-[#FACC15] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="container mx-auto px-4 sm:px-6">
            <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
              <div className="text-4xl mb-4">📦</div>
              <h3 className="text-lg font-bold text-[#001F5F] mb-2">No Products Found</h3>
              <p className="text-sm text-gray-500">
                This brand doesn&apos;t have any products yet.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Full-width grid, cards separated by white gutters (no divider lines) */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 px-2">
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  isDisabled={!product.in_stock}
                  widthClassName="w-full"
                  imageHeightClassName="aspect-[3/4]"
                  imageRadiusClassName="rounded-[4px]"
                  imageSizes="(min-width: 1280px) 16vw, (min-width: 1024px) 20vw, (min-width: 768px) 25vw, (min-width: 640px) 33vw, 50vw"
                />
              ))}
            </div>

            {/* Load more */}
            {totalPages > 1 && (
              <div className="container mx-auto px-4 sm:px-6 flex flex-col items-center justify-center gap-3 mt-8">
                <div className="text-xs text-gray-500">
                  Showing <span className="font-semibold">{products.length}</span> of{' '}
                  <span className="font-semibold">{totalProducts}</span> products
                </div>

                {hasMore && (
                  <Button
                    type="button"
                    variant="default"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={loadingMore}
                    className="min-w-44 h-11 px-6 rounded-lg border border-gray-200 flex items-center justify-center font-bold text-sm"
                  >
                    {loadingMore ? 'Loading...' : 'Load more'}
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Mobile banner 3 after products */}
      {mobileBanners[2] && (
        <div className="md:hidden w-full pt-8 relative top-1">
          <BrandBanner banner={mobileBanners[2]} />
        </div>
      )}

      {/* Tablet banner 3 after products */}
      {tabletBanners[2] && (
        <div className="hidden md:block lg:hidden w-full pt-8">
          <BrandBanner banner={tabletBanners[2]} />
        </div>
      )}

      {/* Desktop banner 3 after products */}
      {desktopBanners[2] && (
        <div className="hidden lg:block w-full pt-8">
          <BrandBanner banner={desktopBanners[2]} />
        </div>
      )}
    </div>
  )
}
